/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable arrow-body-style */
import omitBy from 'lodash/omitBy';
import isEmpty from 'lodash/isEmpty';
import isBoolean from 'lodash/isBoolean';
import isNumber from 'lodash/isNumber';
import isObjectLike from 'lodash/isObjectLike';
import { commonConstants } from '../constants/common.constants';
import { FormlyFieldConfig } from '@ngx-formly/core';

/**
 * Returns an empty object with the same structure as the backend response
 *
 */
export const backendEmptyResponse = <T>() =>
  Object.freeze({
    items: [],
    links: {
      first: '',
      last: '',
    },
    meta: {
      currentPage: 0,
      itemCount: 0,
      itemsPerPage: 0,
      totalItems: 0,
      totalPages: 0,
    },
  });

/**
 * Returns an object filtered by properties that are not null, undefined or empty
 * object
 *
 * @param object
 */
export const objectWithoutEmptyProperties = (object: any): any => {
  const comparisonFunction = (obj: any) =>
    isBoolean(obj) || isNumber(obj) ? false : isEmpty(obj);
  return omitBy(object, comparisonFunction);
};

/**
 * Returns the property of a JSON Formly config that is being looked for
 *
 * @param key The key of the property to search
 * @param fields The json configuration
 */
export const getFieldFromFormlyConfig = (
  key: string,
  fields: FormlyFieldConfig[]
): FormlyFieldConfig | null => {
  for (let i = 0, len = fields.length; i < len; i++) {
    const f = fields[i];
    if (f.key === key) {
      return f;
    }

    if (f.fieldGroup && !f.key) {
      const cf = getFieldFromFormlyConfig(key, f.fieldGroup);
      if (cf) {
        return cf;
      }
    }
  }
  return null;
};

/**
 * Returns a html template representing a formatted JSON with typed class for
 * each property
 *
 * @param json An object representing a json
 */
export const highlightedJSON = (json: Record<string, unknown>) => {
  if (!isObjectLike(json)) {
    return `<div class="formatted-error">${json}</div>`;
  }
  const jsonHtmlTemplate = (jsonObj: any): any => {
    try {
      return Object.keys(jsonObj)
        .map((key) => {
          const value = jsonObj[key];
          let valueType = typeof value;
          const isSimpleValue =
            [
              commonConstants.STRING,
              commonConstants.NUMBER,
              commonConstants.BOOLEAN,
            ].includes(valueType) || !value;
          if (isSimpleValue && valueType === commonConstants.OBJECT) {
            // @ts-ignore
            valueType = 'null';
          }

          const simpleValue = isSimpleValue
            ? `<span class="${valueType}">${
                valueType === 'string' ? '"' + value + '"' : value
              }</span>`
            : jsonHtmlTemplate(value);

          return `<div data-key="${key}" class="line"><span class="key">${key}:</span>${simpleValue}</div>`;
        })
        .join('');
    } catch (e) {
      return JSON.stringify(json, undefined, 2);
    }
  };
  return `<div class="json">${jsonHtmlTemplate(json)}</div>`;
};

export const groupByKey = (array, key) => {
  return array.reduce((hash, obj) => {
    if (obj[key] === undefined) {
      return hash;
    }
    return Object.assign(hash, {
      [obj[key]]: (hash[obj[key]] || []).concat({
        value: obj.value,
        operator: obj.operator,
        type: obj.type,
        subField: obj.subField,
        always_null: obj.always_null,
        join: obj.join,
        subtype: obj.subtype,
        coid: obj.coid,
      }),
    });
  }, {});
};

/**
 * @Method calculateOverAllProgress
 * @Param territory addresses list: Array
 * @Param progressData: knocked Addresses list: Array
 * @Description: Calculating over all territory progress.
 **/
export const calculateOverAllProgress = (addresses, overallKnocked): number => {
  let result = 0;
  const percentage: string = ((+overallKnocked / addresses) * 100).toFixed(2);
  result = +`${Math.round(+percentage)}`;
  return result;
};
